import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './pages/layout/Layout';
import PricingPage from './pages/pricing/PricingPage';
import PassConfirmation from './pages/employee-password-confirmation/PassConfirmation';
import EmailSent from './pages/email-sent/EmailSent';
import ProcessSuccess from './pages/process-success/ProcessSucess';
import Confirmation from './pages/confirmation/Confirmation';
import { ROUTES } from './common/constants/routes';
import { ClerkSignedOut } from './common/clerk-components';
import { CompanyCreate } from './pages/company-create/CompanyCreate';
import SettingsPage from './pages/settings/SettingsPage';
import { NoCompanyHandler } from './pages/company-create/components/routes/NoCompanyHandler';
import { NoCompanyRedirect } from './common/no-company-redirect/NoCompanyRedirect';
import { PermissionGateRedirect, RolesGateRedirect } from './common/roles-gate';
import { ROLES } from './common/constants/groups';
import { useUserInfo } from './common/hooks/useUserInfo';
import DocumentVault from './pages/document-vault/DocumentVault';
import SigningPage from './pages/signing';
import DocumentsPage from './pages/documents';
import PageNotFound from './pages/page-not-found';
import ProtectedRoute from './common/roles-gate/protected-route';
import BusinessProfile from './pages/business-profile/BusinessProfile';
import EmployeeProfiles from './pages/employee-profiles/EmployeeProfiles';
import EmployeeProfile from './pages/employee-profiles/EmployeeProfile';
import { useCountriesInfo } from './common/hooks/useCountries';
import SupplierProfiles from './pages/supplier-profiles/SupplierProfiles';
import SupplierProfile from './pages/supplier-profiles/SupplierProfile';
import CustomerProfiles from './pages/customer-profiles/CustomerProfiles';
import CustomerProfile from './pages/customer-profiles/CustomerProfile';
import { CreateCustomerProfile } from './pages/customer-profiles/CreateCustomerProfile';
import { CreateSupplierProfile } from './pages/supplier-profiles/CreateSupplierProfile';
import DocumentDetails from './pages/document-details/DocumentDetails';
import LibraryItemDetails from './pages/library-item-details/LibraryItemDetails';
import { useAuth } from '@clerk/clerk-react';
import EmployeeProfilePageForAdmin from './pages/employee-profiles/EmployeeProfilePageForAdmin';
import HomePage from './pages/home/HomePage';
import Library from './pages/library/Library';
import LogIn from './pages/login/LogIn';
import SignUp from './pages/signup/SignUp';
import DocumentsKeyDates from './pages/documents-key-dates/DocumentsKeyDates';
import { USER_PERMISSIONS } from './common/constants/permissions';

const App = () => {
  const location = useLocation();
  const { isSignedIn } = useAuth();
  useUserInfo().catch();
  useCountriesInfo();
  useEffect(() => {
    const head = document.head || document.getElementsByTagName('head')[0];
    const linkToRemove = document.getElementById('specialCanonicalLink');
    if (document.getElementById('specialCanonicalLink')) {
      head.removeChild(linkToRemove);
    }
    if (location.pathname.includes(ROUTES.LOGIN) || location.pathname.includes(ROUTES.SIGN_UP)) {
      const linkElement = document.createElement('link');
      linkElement.rel = 'canonical';
      linkElement.href = location.pathname.includes(ROUTES.LOGIN)
        ? 'https://businesskitz.com/login'
        : 'https://businesskitz.com/signup';
      linkElement.id = 'specialCanonicalLink';
      head.appendChild(linkElement);
    }
    if (location.pathname !== ROUTES.LOGIN && location.pathname !== ROUTES.SIGN_UP) {
      localStorage.setItem('location', location.pathname);
    }
  }, [location.pathname]);
  return (
    <Routes>
      <Route path='*' element={<PageNotFound />} />
      <Route
        path={ROUTES.INDEX}
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          path={ROUTES.BUSINESS_PROFILE}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_BUSINESS_PROFILE_READ}>
                <NoCompanyRedirect>
                  <BusinessProfile />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SIGNING_DOCUMENT}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_DOCUMENT_SIGN_READ}>
                <NoCompanyRedirect>
                  <SigningPage />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENTS}
          element={
            <ProtectedRoute>
              <RolesGateRedirect role={ROLES.ADMIN}>
                <NoCompanyRedirect>
                  <DocumentsPage />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENT_VAULT}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_DOCUMENT_VIEW_ALL}>
                <NoCompanyRedirect>
                  <DocumentVault />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENTS_KEY_DATES}
          element={
            <ProtectedRoute>
              <RolesGateRedirect role={ROLES.ADMIN}>
                <NoCompanyRedirect>
                  <DocumentsKeyDates />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENT_VAULT_CATEGORY_DOCUMENT}
          element={
            <ProtectedRoute>
              <RolesGateRedirect role={ROLES.ADMIN}>
                <NoCompanyRedirect>
                  <DocumentDetails />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        {/*<Route
          path={ROUTES.WORKSPACE_DOCUMENT}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <DocumentDetails />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />*/}
        <Route
          path={ROUTES.PRICING}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_SUBSCRIPTION_MANAGE}>
                <PricingPage />
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EMPLOYEE_PROFILES}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_READ}>
                <NoCompanyRedirect>
                  <EmployeeProfiles />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EMPLOYEE_PROFILE}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_READ}>
                <NoCompanyRedirect>
                  <EmployeeProfile />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EMPLOYEE_PROFILE_CREATE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect role={ROLES.ADMIN}>
                <NoCompanyRedirect>
                  <EmployeeProfilePageForAdmin />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPLIER_PROFILES}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_READ}>
                <NoCompanyRedirect>
                  <SupplierProfiles />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPLIER_PROFILE_CREATE}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_CREATE}>
                <NoCompanyRedirect>
                  <CreateSupplierProfile />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPLIER_PROFILE}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_READ}>
                <NoCompanyRedirect>
                  <SupplierProfile />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMER_PROFILES}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_READ}>
                <NoCompanyRedirect>
                  <CustomerProfiles />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMER_PROFILE_CREATE}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_CREATE}>
                <NoCompanyRedirect>
                  <CreateCustomerProfile />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMER_PROFILE}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect permission={USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_READ}>
                <NoCompanyRedirect>
                  <CustomerProfile />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENT_LIBRARY}
          element={
            <ProtectedRoute>
              <PermissionGateRedirect
                permission={[
                  USER_PERMISSIONS.ORG_DOCUMENT_LIBRARY_READ,
                  USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_READ,
                ]}
              >
                <NoCompanyRedirect>
                  <Library />
                </NoCompanyRedirect>
              </PermissionGateRedirect>
            </ProtectedRoute>
          }
        />
        {/*<Route
          path={ROUTES.WORKSPACE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <Workspace />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />*/}
        <Route
          path={ROUTES.TEMPLATE_ITEM}
          element={
            <ProtectedRoute>
              <RolesGateRedirect role={ROLES.ADMIN}>
                <NoCompanyRedirect>
                  <LibraryItemDetails />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route path={ROUTES.ACCEPTED_INVITE} element={<SignUp />} />
        <Route path={ROUTES.EMAIL_SENT} element={<EmailSent />} />
        <Route path={ROUTES.PROCESS_SUCCESS} element={<ProcessSuccess text={'Confirmed.'} />} />
        <Route
          path={ROUTES.SETTINGS}
          element={
            <ProtectedRoute>
              <RolesGateRedirect role={ROLES.ADMIN}>
                <SettingsPage />
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route path={ROUTES.CONFIRMATION} element={<Confirmation />} />
      </Route>
      <Route path={ROUTES.JOIN_COMPANY} element={<PassConfirmation />} />
      <Route
        path={ROUTES.LOGIN}
        element={
          isSignedIn ? (
            <Navigate to={ROUTES.INDEX} />
          ) : (
            <ClerkSignedOut>
              <LogIn />
            </ClerkSignedOut>
          )
        }
      />
      <Route path={ROUTES.SIGN_UP} element={<SignUp />} />
      {/*<Route path={ROUTES.ACCEPTED_INVITE} element={<ProcessSuccess text={'Accepted'} />} />*/}
      <Route
        path={ROUTES.COMPANY_CREATE}
        element={
          <NoCompanyHandler>
            <RolesGateRedirect role={ROLES.ADMIN}>
              <CompanyCreate />
            </RolesGateRedirect>
          </NoCompanyHandler>
        }
      />
    </Routes>
  );
};

export default App;
